import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  memo,
} from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useOrder } from "../context/OrderContext";
import { useConfig } from "../context/ConfigContext";
import ActiveOrderBadge from "./ActiveOrderBadge";
import CookingBadge from "./CookingBadge";
import RecipePopup from "./RecipePopup";
import { formatPrice } from "../utils/currency";
import "./Dashboard.css";
import { useNavigate } from "react-router-dom";
import { API_URL, API_BASE_URL, VENUE_ID } from "../config/constants";
import useEmblaCarousel from "embla-carousel-react";

// Cache-first: in-memory recipes cache (persists across re-renders/reconnects)
const recipesCache = { data: [], timestamp: 0 };
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min - use cache if fresh

const FALLBACK_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Cdefs%3E%3ClinearGradient id='bg' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23f5e6d3'/%3E%3Cstop offset='50%25' stop-color='%23e8d5c4'/%3E%3Cstop offset='100%25' stop-color='%23c4956a'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23bg)' width='400' height='300'/%3E%3Cpath fill='%234a3728' opacity='0.4' d='M180 100h40v120h-40z'/%3E%3Cellipse fill='%234a3728' opacity='0.5' cx='200' cy='95' rx='55' ry='25'/%3E%3Cpath fill='%23654a32' opacity='0.6' d='M165 95 Q200 70 235 95'/%3E%3Ctext fill='%234a3728' font-family='sans-serif' font-size='28' font-weight='bold' x='200' y='260' text-anchor='middle' opacity='0.7'%3E☕ Barista%3C/text%3E%3C/svg%3E";

const CarouselCard = memo(
  ({
    recipe,
    hasInventoryWarning,
    isDisabled,
    currency,
    onRecipeClick,
    onImageError,
  }) => (
    <div className="embla__slide">
      <div
        className={`carousel-card ${isDisabled ? "disabled" : ""}`}
        onClick={() => !isDisabled && onRecipeClick(recipe)}
      >
        <div className="card-image">
          <img
            src={recipe.recipe_image || FALLBACK_IMG}
            alt={recipe.recipe_name}
            loading="lazy"
            decoding="async"
            onError={onImageError}
          />
          <div className="card-overlay">
            {recipe.recipe_category && (
              <span className="card-category">{recipe.recipe_category}</span>
            )}
            <span className="card-cook-time">
              ⏱ {recipe.preparation_time || 0}m
            </span>
          </div>
          {hasInventoryWarning && (
            <span className="card-low-stock">Low stock</span>
          )}
        </div>
        <div className="card-content">
          {recipe.recipe_category && (
            <span className="card-content-category">{recipe.recipe_category}</span>
          )}
          <h3 className="card-title">{recipe.recipe_name}</h3>
          <div className="card-footer">
            <span className="card-price">
              {formatPrice(recipe.recipe_price, currency)}
            </span>
          </div>
        </div>
        {isDisabled && <div className="disabled-overlay" />}
      </div>
    </div>
  ),
);
CarouselCard.displayName = "CarouselCard";

// Memoized carousel - isolates from OrderContext (actionProgress, cart) re-renders
const RecipeCarousel = memo(
  ({
    filteredRecipes,
    inventoryWarnings,
    kitchenReady,
    isProcessing,
    currency,
    onRecipeClick,
    onImageError,
  }) => {
    const emblaOptions = useMemo(
      () => ({
        loop: true,
        align: "center",
        dragFree: false,
        skipSnaps: false,
        slidesToScroll: 1,
      }),
      [],
    );
    const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);
    const [selectedSlide, setSelectedSlide] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState([]);
    const prevSnapRef = useRef(-1);

    const handlePrev = useCallback(() => {
      if (!emblaApi) return;
      emblaApi.scrollPrev();
    }, [emblaApi]);

    const handleNext = useCallback(() => {
      if (!emblaApi) return;
      emblaApi.scrollNext();
    }, [emblaApi]);

    const handleDotClick = useCallback(
      (idx) => {
        if (!emblaApi) return;
        emblaApi.scrollTo(idx);
      },
      [emblaApi],
    );

    useEffect(() => {
      if (!emblaApi) return;
      const onSelect = () => {
        const snap = emblaApi.selectedScrollSnap();
        if (prevSnapRef.current !== snap) {
          prevSnapRef.current = snap;
          setSelectedSlide(snap);
        }
      };
      const onReInit = () => {
        const snaps = emblaApi.scrollSnapList();
        setScrollSnaps((prev) =>
          prev.length !== snaps.length || prev.some((v, i) => v !== snaps[i])
            ? snaps
            : prev,
        );
        onSelect();
      };
      prevSnapRef.current = emblaApi.selectedScrollSnap();
      setScrollSnaps(emblaApi.scrollSnapList());
      setSelectedSlide(emblaApi.selectedScrollSnap());
      emblaApi.on("select", onSelect);
      emblaApi.on("reInit", onReInit);
      return () => {
        emblaApi.off("select", onSelect);
        emblaApi.off("reInit", onReInit);
      };
    }, [emblaApi, filteredRecipes.length]);

    useEffect(() => {
      if (!emblaApi) return;
      emblaApi.scrollTo(0, true);
    }, [emblaApi, filteredRecipes.length]);

    return (
      <>
        <div className="carousel-container">
          <button
            className="carousel-btn prev"
            onClick={handlePrev}
            disabled={!emblaApi || filteredRecipes.length <= 1}
          >
            ‹
          </button>
          <div className="embla">
            <div className="embla__viewport" ref={emblaRef}>
              <div className="embla__container">
                {filteredRecipes.map((recipe) => (
                  <CarouselCard
                    key={recipe.recipe_id}
                    recipe={recipe}
                    hasInventoryWarning={!!inventoryWarnings[recipe.recipe_id]}
                    isDisabled={
                      !recipe.is_available ||
                      !kitchenReady ||
                      !!inventoryWarnings[recipe.recipe_id]
                    }
                    currency={currency}
                    onRecipeClick={onRecipeClick}
                    onImageError={onImageError}
                  />
                ))}
              </div>
            </div>
          </div>
          <button
            className="carousel-btn next"
            onClick={handleNext}
            disabled={!emblaApi || filteredRecipes.length <= 1}
          >
            ›
          </button>
        </div>
        {filteredRecipes.length > 1 && scrollSnaps.length > 0 && (
          <div className="carousel-dots">
            {scrollSnaps.map((_, idx) => (
              <span
                key={idx}
                className={`dot ${idx === selectedSlide ? "active" : ""}`}
                onClick={() => handleDotClick(idx)}
              />
            ))}
          </div>
        )}
      </>
    );
  },
);
RecipeCarousel.displayName = "RecipeCarousel";

const Dashboard = () => {
  const { currency } = useConfig();
  const [recipes, setRecipes] = useState(() => recipesCache.data);
  const [loading, setLoading] = useState(!recipesCache.data.length);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [kitchenReady, setKitchenReady] = useState(true);
  const [inventoryWarnings, setInventoryWarnings] = useState({});
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [selectedSlide, setSelectedSlide] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const navigate = useNavigate();
  const { isProcessing, activeOrder, socket, cart } = useOrder();
  const [showProcessingOverlay, setShowProcessingOverlay] = useState(true);

  useEffect(() => {
    if (isProcessing) setShowProcessingOverlay(true);
  }, [isProcessing]);

  const recipesRef = useRef([]);
  const inventoryWarningsRef = useRef({});
  const kitchenReadyRef = useRef(true);
  const lastInventoryCheckRef = useRef(0);
  const kitchenReadyDebounceRef = useRef({ timer: null, pending: null });
  const statusCheckInFlightRef = useRef(false);
  const statusFailureCountRef = useRef(0);
  const statusRetryTimerRef = useRef(null);
  const INVENTORY_CHECK_MIN_INTERVAL_MS = 30000;
  const KITCHEN_READY_DEBOUNCE_MS = 2000; // Need 2s stable value before switching (prevents carousel/sleeping flip-flop)
  const SLEEP_AFTER_FAILURES = 3; // Show sleep screen only after N consecutive failures (avoids flip-flop from canceled/timeout)
  const STATUS_RETRY_DELAY_MS = 3000; // Retry after failure before next interval

  // Update refs when state changes
  useEffect(() => {
    recipesRef.current = recipes;
    inventoryWarningsRef.current = inventoryWarnings;
    kitchenReadyRef.current = kitchenReady;
  }, [recipes, inventoryWarnings, kitchenReady]);

  // Cleanup kitchenReady debounce timer on unmount
  useEffect(() => {
    return () => {
      const t = kitchenReadyDebounceRef.current?.timer;
      if (t) clearTimeout(t);
    };
  }, []);

  const checkInventoryForRecipes = useCallback(
    async (recipesToCheck = recipesRef.current, force = false) => {
      if (recipesToCheck.length === 0) return;
      const now = Date.now();
      if (
        !force &&
        now - lastInventoryCheckRef.current < INVENTORY_CHECK_MIN_INTERVAL_MS
      ) {
        return; // Skip - too soon since last check
      }
      lastInventoryCheckRef.current = now;

      const warnings = {};
      const recipeIds = recipesToCheck.map((r) => r.recipe_id);
      if (recipeIds.length === 0) return;

      try {
        const invUrl = `${API_URL}/graphql`;
        const response = await axios.post(
          invUrl,
          {
            query: `
              query($recipe_ids: [Int], $quantity: Int) {
                checkRecipesInventoryBatch(recipe_ids: $recipe_ids, quantity: $quantity)
              }
            `,
            variables: { recipe_ids: recipeIds, quantity: 1 },
          },
          { timeout: 15000 },
        );

        const raw = response.data.data?.checkRecipesInventoryBatch || "{}";
        let batchResults;
        try {
          batchResults = typeof raw === "string" ? JSON.parse(raw) : raw;
        } catch (e) {
          batchResults = {};
        }

        for (const recipe of recipesToCheck) {
          const result = batchResults[recipe.recipe_id];
          if (result && !result.available) {
            warnings[recipe.recipe_id] = result;
            console.debug(
              `[Inventory] ${recipe.recipe_name} (id=${recipe.recipe_id}) disabled:`,
              result.reason,
              result.missing ? `missing: ${result.missing.join(", ")}` : "",
              result.low ? `low: ${JSON.stringify(result.low)}` : "",
            );
          }
        }
      } catch (error) {
        console.debug(
          `[Inventory] batch check failed:`,
          error?.message,
        );
      }

      const warningsStr = JSON.stringify(warnings);
      const currentWarningsStr = JSON.stringify(inventoryWarningsRef.current);
      if (warningsStr !== currentWarningsStr) {
        setInventoryWarnings(warnings);
      }
    },
    [],
  );

  const setKitchenReadyDebounced = useCallback((connected) => {
    const current = kitchenReadyRef.current;
    if (connected === current) return;
    const deb = kitchenReadyDebounceRef.current;
    if (deb.timer) clearTimeout(deb.timer);
    deb.pending = connected;
    deb.timer = setTimeout(() => {
      deb.timer = null;
      deb.pending = null;
      setKitchenReady(connected);
    }, KITCHEN_READY_DEBOUNCE_MS);
  }, []);

  const checkKitchenStatus = useCallback(
    async (signal, onRetry, resetFailures = false) => {
      if (statusCheckInFlightRef.current) return;
      if (resetFailures) statusFailureCountRef.current = 0;
      statusCheckInFlightRef.current = true;
      try {
        const response = await axios.get(`${API_BASE_URL}/api/kitchen/status`, {
          timeout: 5000,
          signal: signal || undefined,
        });
        statusFailureCountRef.current = 0;
        const connected = response.data.kitchenConnected ?? response.data.ready;
        setKitchenReadyDebounced(connected);
      } catch (error) {
        if (error?.name !== "CanceledError" && error?.code !== "ERR_CANCELED") {
          statusFailureCountRef.current += 1;
          if (statusFailureCountRef.current >= SLEEP_AFTER_FAILURES) {
            setKitchenReadyDebounced(false);
          } else if (onRetry) {
            onRetry();
          }
        }
      } finally {
        statusCheckInFlightRef.current = false;
      }
    },
    [setKitchenReadyDebounced],
  );

  const fetchRecipes = useCallback(
    async (forceRefresh = false) => {
      const useCache =
        !forceRefresh &&
        recipesCache.data.length > 0 &&
        Date.now() - recipesCache.timestamp < CACHE_TTL_MS;
      if (useCache) {
        setRecipes(recipesCache.data);
        setLoading(false);
        setError(null);
        setTimeout(() => checkInventoryForRecipes(recipesCache.data), 100);
        return;
      }

      try {
        if (!recipesCache.data.length) setLoading(true);
        setError(null);
        const requestUrl = `${API_URL}/graphql`;
        const response = await axios.post(
          requestUrl,
          {
            query: `
          query($venueId: Int) {
            recipes(venue_id: $venueId) {
              recipe_id
              recipe_name
              recipe_category
              recipe_ingredients
              recipe_image
              recipe_price
              preparation_time
              is_available
            }
          }
        `,
            variables: { venueId: VENUE_ID },
          },
          { timeout: 10000 },
        );

        if (response.data.errors) {
          const errorMessage =
            response.data.errors[0]?.message || "Failed to load menu";
          setError(errorMessage);
          toast.error(`Menu Error: ${errorMessage}`);
          return;
        }

        if (response.data.data?.recipes) {
          const uniqueRecipes = response.data.data.recipes.filter(
            (recipe, index, self) =>
              index === self.findIndex((r) => r.recipe_id === recipe.recipe_id),
          );
          recipesCache.data = uniqueRecipes;
          recipesCache.timestamp = Date.now();
          setRecipes(uniqueRecipes);
          setError(null);
          setTimeout(() => {
            checkInventoryForRecipes(uniqueRecipes);
          }, 500);
        } else {
          setError("No menu items available");
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Unable to connect to server";
        setError(errorMessage);
        console.error("Failed to fetch recipes:", error);
      } finally {
        setLoading(false);
      }
    },
    [checkInventoryForRecipes],
  );

  // Initial load - run once on mount (socket may be null initially)
  useEffect(() => {
    fetchRecipes(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Socket handlers - separate effect, only register when socket available
  useEffect(() => {
    if (!socket) return;

    // Use ONLY queueStatusUpdate - it has kitchenConnected. kitchenStatusUpdate has "ready" (connected && !busy)
    // which flips to false when robot is busy, causing carousel/sleeping toggle
    const handleQueueStatusUpdate = (data) => {
      if (data?.venue_id != null && data.venue_id !== VENUE_ID) return;
      const connected = data?.kitchenConnected;
      if (data && connected !== undefined)
        setKitchenReadyDebounced(!!connected);
    };

    socket.on("queueStatusUpdate", handleQueueStatusUpdate);

    return () => {
      socket.off("queueStatusUpdate", handleQueueStatusUpdate);
    };
  }, [socket]);

  useEffect(() => {
    let ac = null;
    const scheduleRetry = () => {
      if (statusRetryTimerRef.current) return;
      statusRetryTimerRef.current = setTimeout(() => {
        statusRetryTimerRef.current = null;
        ac = new AbortController();
        checkKitchenStatus(ac.signal, scheduleRetry);
      }, STATUS_RETRY_DELAY_MS);
    };
    const runCheck = () => {
      ac = new AbortController();
      checkKitchenStatus(ac.signal, scheduleRetry);
    };
    runCheck();
    const interval = setInterval(runCheck, 30000);
    return () => {
      clearInterval(interval);
      if (statusRetryTimerRef.current) {
        clearTimeout(statusRetryTimerRef.current);
        statusRetryTimerRef.current = null;
      }
      if (ac) ac.abort();
    };
  }, [checkKitchenStatus]);

  const filteredRecipes = useMemo(() => {
    if (!searchQuery.trim()) return recipes;
    const q = searchQuery.trim().toLowerCase();
    return recipes.filter(
      (r) =>
        (r.recipe_name || "").toLowerCase().includes(q) ||
        (r.recipe_category || "").toLowerCase().includes(q),
    );
  }, [recipes, searchQuery]);

  const handleRecipeClick = useCallback(
    (recipe) => {
      // Allow adding items even when order in progress - new order will queue

      if (!kitchenReadyRef.current) {
        toast.error("Kitchen server is not ready. Please wait.");
        return;
      }

      const warnings = inventoryWarningsRef.current;
      if (warnings[recipe.recipe_id]) {
        return; // Card is disabled, don't show popup
      }

      setSelectedRecipe(recipe);
    },
    [],
  );

  const handleClosePopup = useCallback(() => {
    setSelectedRecipe(null);
  }, []);

  const handleCheckout = useCallback(() => {
    setSelectedRecipe(null);
    navigate("/checkout");
  }, [navigate]);

  const handleImageError = useCallback((e) => {
    e.target.src = FALLBACK_IMG;
  }, []);

  const totalCartItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="dashboard">
      <ActiveOrderBadge />
      <CookingBadge />

      {/* Header */}
      <div className="dashboard-header">
        <div className="brand">
          <span className="brand-icon">☕</span>
          <h1>AutoChef Barista </h1>
        </div>
        {totalCartItems > 0 && (
          <button className="cart-badge" onClick={() => navigate("/checkout")}>
            <svg
              className="cart-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
            <span className="cart-count">{totalCartItems}</span>
          </button>
        )}
      </div>

      {/* Search Bar - kiosk: focus opens on-screen keyboard */}
      <div className="search-bar-wrap">
        <div className="search-bar">
          <span className="search-icon">🔍</span>
          <input
            type="search"
            inputMode="search"
            autoComplete="off"
            placeholder="Search drinks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button
              type="button"
              className="search-clear"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Carousel */}
      {loading ? (
        <div className="loading">
          <div className="loading-spinner">☕</div>
          <p>Loading menu...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Unable to Load Menu</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => fetchRecipes(true)}>
            🔄 Retry
          </button>
        </div>
      ) : !kitchenReady ? (
        <div className="offline-state">
          <div className="offline-card">
            <div className="offline-icon" aria-hidden="true">☕</div>
            <h2>Cafe is Sleeping</h2>
            <p className="offline-lead">
              Our robot barista is taking a short break. We’ll wake up soon.
            </p>
            <div className="offline-hint">
              If you’re staff: check Kitchen Server and robot connection.
            </div>
            <div className="offline-actions">
              <button
                type="button"
                className="offline-btn offline-btn-primary"
                onClick={() => checkKitchenStatus(undefined, undefined, true)}
              >
                <span className="offline-btn-icon">🔄</span>
                <span>Check Again</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <RecipeCarousel
          filteredRecipes={filteredRecipes}
          inventoryWarnings={inventoryWarnings}
          kitchenReady={kitchenReady}
          isProcessing={isProcessing}
          currency={currency}
          onRecipeClick={handleRecipeClick}
          onImageError={handleImageError}
        />
      )}

      {/* Recipe Popup */}
      {selectedRecipe && (
        <RecipePopup
          recipe={selectedRecipe}
          onClose={handleClosePopup}
          onCheckout={handleCheckout}
          currency={currency}
        />
      )}

      {/* Processing Overlay – can be dismissed with Back to menu; payment stays disabled until order completes */}
      {isProcessing && showProcessingOverlay && (
        <div className="processing-overlay">
          <div className="processing-content">
            <div className="processing-spinner">⏳</div>
            <p className="processing-text">Order Processing...</p>
            <div className="processing-actions">
              <button
                className="view-order-btn"
                onClick={() => navigate(`/order-status/${activeOrder?.order_id}`)}
              >
                View Progress →
              </button>
              <button
                className="back-to-menu-btn"
                onClick={() => setShowProcessingOverlay(false)}
              >
                Back to menu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
